import { BinanceAPI_SPOT_ticker_24hr } from '@/lib/api/spot'
import { BinanceAPI_SPOT_ticker_24hr_Res } from '@/types/binanceAPI'

import { spotHeaderInfo, spotTickerArray } from '@/types/spot'
import { SpotWebSocketTicker } from '@/types/spot/ws'
import { ConvetDotSize } from '@/utils/formatter'
import { MutableRefObject } from 'react'
import { create } from 'zustand'

import { IsSpotAssetInfo, Spot_ConvertPriceTodotSize, storeSpotExchangeInfo } from '@/stores/spot/exchange'
import { throttle } from '@/utils/datetimes'
import { storeSpotCtrl } from '../spotctrl'

function subscribeTicker(webSocket: WebSocket) {
  const message = {
    method: 'SUBSCRIBE',
    params: ['!ticker@arr'], // 배열로 설정
    id: 1,
  }
  webSocket.send(JSON.stringify(message))
}

function mapWebSocketToAPIData(webSocketData: SpotWebSocketTicker): BinanceAPI_SPOT_ticker_24hr_Res {
  return {
    symbol: webSocketData.s, // 종목명
    priceChange: webSocketData.p, // 가격 변화량
    priceChangePercent: webSocketData.P, // 가격 변화율
    weightedAvgPrice: webSocketData.w, // 가중 평균가
    prevClosePrice: webSocketData.x, // 이전 종가
    lastPrice: webSocketData.c, // 마지막 거래 가격
    lastQty: webSocketData.Q, // 마지막 거래량
    bidPrice: webSocketData.b, // 매수 호가
    bidQty: webSocketData.B, // 매수 잔량
    askPrice: webSocketData.a, // 매도 호가
    askQty: webSocketData.A, // 매도 잔량
    openPrice: webSocketData.o, // 시가
    highPrice: webSocketData.h, // 고가
    lowPrice: webSocketData.l, // 저가
    volume: webSocketData.v, // 거래량
    quoteVolume: webSocketData.q, // 거래 대금
    openTime: webSocketData.O, // 시장 개장 시간
    closeTime: webSocketData.C, // 시장 마감 시간
    firstId: webSocketData.F, // 첫 번째 거래 ID
    lastId: webSocketData.L, // 마지막 거래 ID
    count: webSocketData.n, // 거래 횟수
  } as BinanceAPI_SPOT_ticker_24hr_Res
}

function mapWebSocketDataToHeader(ticker: SpotWebSocketTicker): spotHeaderInfo {
  let pricesize = 2
  let usdtsize = 2
  let persent = 2
  const ticksize = storeSpotExchangeInfo.getState().getTickInfo(ticker.s)
  if (ticksize !== null) {
    pricesize = ticksize?.priceTick || 2
  }

  return {
    symbol: ticker.s, // 종목명 (예: "BNBUSDT")
    price: ConvetDotSize(ticker.c, pricesize), // 마지막 거래 가격
    persent: ConvetDotSize(ticker.P, persent), // 가격 변화율
    diff: ConvetDotSize(ticker.p, pricesize), // 가격 변화량
    high: ConvetDotSize(ticker.h, pricesize), // 고가
    low: ConvetDotSize(ticker.l, pricesize), // 저가
    volume: ConvetDotSize(ticker.v, usdtsize), // 거래량 (USDT)
    volumeUSDT: ConvetDotSize(ticker.q, usdtsize), // 거래량 (USDT)
    markPrice: ConvetDotSize(ticker.c, pricesize), // 마크 가격 (여기서는 마지막 거래 가격을 사용)
  }
}

const defaultHeader: spotHeaderInfo = {
  symbol: '',
  price: '',
  persent: '',
  diff: '',
  high: '',
  low: '',
  volume: '',
  volumeUSDT: '',
  markPrice: '',
}

interface BiananceSpotTicker {
  isConnected: boolean
  retryCount: number
  wsSocket: MutableRefObject<WebSocket> | null
  ticker24Map: Map<string, BinanceAPI_SPOT_ticker_24hr_Res>
  ticker24Arr: spotTickerArray[]
  curPrice: string // 종합데이터로 변경될 수 있음
  curPriceTick: string
  curCoinTick: string
  headerInfo: spotHeaderInfo | null

  binanceSpotTickerInit: () => void

  // binanace ticker
  APIBinanceTicker: () => void // ticker 데이터 가져오기
  setTicker24: (args: BinanceAPI_SPOT_ticker_24hr_Res[]) => void
  setTicker24Arr: (args: BinanceAPI_SPOT_ticker_24hr_Res[]) => void
  setWSTicker24Map: (args: SpotWebSocketTicker[]) => void

  // sysmbol 변경
  setBiananceSpotSymbol: (symbol: string) => void
  getHeaderForTicker: (symbol: string) => spotHeaderInfo | null
  setWSHeader: (args: SpotWebSocketTicker) => void // 헤더 데이터 처리

  wsInit: () => void
  // wsConnection: () => void, // 연결시도
  wsOpen: (socket: WebSocket) => void // 오픈시 동작
  wsClose: () => void // 종료시 리턴
  wsError: (error: string) => void // 에러시 리턴
  wsMessage: (data: any) => void // 메시지 데이터
}

export const storeBinanceSpotTicker = create<BiananceSpotTicker>((set, get) => ({
  // 초기 상태
  errorMessage: null,
  isConnected: false,
  retryCount: 0,
  wsSocket: null,
  ticker24Map: new Map(),
  ticker24Arr: [],
  spotSymbol: 'BTCUSDT',
  curPrice: '',
  curPriceTick: '',
  curCoinTick: '',
  headerInfo: defaultHeader,

  // 초기화
  binanceSpotTickerInit: () => {
    get().APIBinanceTicker()
  },

  // API 데이터 가져오기
  APIBinanceTicker: async () => {
    const res = await BinanceAPI_SPOT_ticker_24hr() // 데이터 가져오는 API 호출
    get().setTicker24(res)
    get().setTicker24Arr(res)
  },

  // ticker 데이터를 Map에 저장
  setTicker24: (args) => {
    const data = new Map<string, BinanceAPI_SPOT_ticker_24hr_Res>()
    args.forEach((el) => {
      if (IsSpotAssetInfo(el.symbol) && el.symbol.endsWith('USDT')) {
        data.set(el.symbol, el)
      }
    })

    const curPrice =  String(data.get(storeSpotCtrl.getState().spotSymbol)?.lastPrice)

    set({ ticker24Map: data, curPrice })
  },

  // ticker 데이터를 배열로 저장
  setTicker24Arr: (args) => {
    const tmp: spotTickerArray[] = []
    const tmpheader: spotHeaderInfo = { symbol: '', price: '', persent: '', diff: '', high: '', low: '', volume: '', volumeUSDT: '', markPrice: '' }
    args.forEach((el) => {
      if (IsSpotAssetInfo(el.symbol) && el.symbol.endsWith('USDT')) {
        tmp.push({
          symbol: el.symbol,
          price: Spot_ConvertPriceTodotSize(el.symbol, Number(el.lastPrice)),
          rate: ConvetDotSize(el.priceChangePercent, 2),
          quoteVolume: el.quoteVolume,
          Favorites: false,
        })
      }
      const spotSymbol = storeSpotCtrl.getState().spotSymbol
      if (el.symbol === spotSymbol) {
        let pricesize = 2
        const ticksize = storeSpotExchangeInfo.getState().getTickInfo(el.symbol)
        if (ticksize !== null) {
          pricesize = ticksize?.priceTick || 2
        }
        tmpheader.symbol = el.symbol
        tmpheader.price = ConvetDotSize(el.lastPrice, pricesize)
        tmpheader.persent = el.priceChangePercent
        tmpheader.diff = ConvetDotSize(el.priceChange, pricesize)
        tmpheader.high = ConvetDotSize(el.highPrice, pricesize)
        tmpheader.low = ConvetDotSize(el.lowPrice, pricesize)
        tmpheader.volume = ConvetDotSize(el.volume, 2)
        tmpheader.volumeUSDT = ConvetDotSize(el.quoteVolume, 2)
        tmpheader.markPrice = ConvetDotSize(el.lastPrice, pricesize)
      }
    })
    set({ ticker24Arr: tmp, headerInfo: tmpheader })
  },

  // WebSocket 데이터 처리
  setWSTicker24Map: throttle((args) => {
    if (!args || args.length === 0) return

    const tikcermap = new Map(get().ticker24Map)

    args.forEach((el) => {
      if (el.e === '24hrTicker' && el.s.endsWith('USDT')) {
        tikcermap.set(el.s, mapWebSocketToAPIData(el))
        const spotSymbol = storeSpotCtrl.getState().spotSymbol
        if (el.s === spotSymbol) {
          get().setWSHeader(el)
        }
      }
    })

    set({ ticker24Map: tikcermap })
  }, 500),

  setBiananceSpotSymbol: (symbol) => {
    const header = get().getHeaderForTicker(symbol)
    if (header) {
      set({ headerInfo: header })
    }
  },

  getHeaderForTicker: (symbol) => {
    const tickerMap = get().ticker24Map
    const oneTicker = tickerMap.get(symbol)
    if (!oneTicker) return null

    const filters = storeSpotExchangeInfo.getState().getTickInfo(symbol)

    if (!filters) return null

    return {
      symbol: oneTicker.symbol,
      price: ConvetDotSize(oneTicker.lastPrice, filters.priceTick),
      persent: ConvetDotSize(oneTicker.priceChangePercent, 2),
      diff: ConvetDotSize(oneTicker.priceChange, filters.priceTick),
      high: ConvetDotSize(oneTicker.highPrice, filters.priceTick),
      low: ConvetDotSize(oneTicker.lowPrice, filters.priceTick),
      volume: ConvetDotSize(oneTicker.volume, 2),
      volumeUSDT: ConvetDotSize(oneTicker.quoteVolume, 2),
      markPrice: ConvetDotSize(oneTicker.lastPrice, filters.priceTick),
    }
  },

  setWSHeader: (args) => {
    const tmpheader = mapWebSocketDataToHeader(args)
    set({ headerInfo: tmpheader })
  },

  wsInit: () => {
    if (get().wsSocket?.current) {
      console.log('[WS Spot] is already connected')
      return
    }

    const socket = new WebSocket(process.env.NEXT_PUBLIC_BIANANCE_SPOT_SOCKET_URL)
    socket.onopen = () => get().wsOpen(socket)
    socket.onclose = () => get().wsClose()
    socket.onerror = (event) => get().wsError((event as ErrorEvent).message)
    socket.onmessage = (event) => get().wsMessage(event.data)

    set({ wsSocket: { current: socket } })
  },

  wsOpen: (socket) => {
    subscribeTicker(socket)
    set({ isConnected: true, retryCount: 0 })
    console.log('[WS Spot] connected')
  },

  wsClose: () => {
    const socket = get().wsSocket?.current

    if (socket) {
      // WebSocket 연결 종료
      socket.close()

      // 이벤트 리스너들 해제
      socket.onclose = null
      socket.onerror = null
      socket.onmessage = null

      // 상태 업데이트: 연결 상태 false로 설정
      set({
        wsSocket: null, // 연결 종료 후 WebSocket 객체 초기화
        isConnected: false,
        retryCount: 0,
      })
      console.log('[WS Spot] unconnected')
    }
  },

  wsError: (error) => {
    set({ isConnected: false, wsSocket: null })
    const retryLimit = 5
    const currentRetries = get().retryCount || 0
    if (currentRetries < retryLimit) {
      setTimeout(() => {
        set({ retryCount: currentRetries + 1 })
        get().wsInit()
      }, 1000)
    }
  },

  wsMessage: (message) => {
    const data = JSON.parse(message)
    if (!Array.isArray(data)) {
      if (data?.result === null || data?.result === undefined) return
      if (data?.e !== null) {
        get().setWSTicker24Map([data])
      }
    } else {
      get().setWSTicker24Map(data)
    }
  },
}))

// export const storeBinanceSpotTicker = create<BiananceSpotTicker>()(
//   persist(
//     (set, get) => ({
//       ...(loadInitialState() as BiananceSpotTicker),

//       binanceSpotTickerInit: () => {
//         // 구현 필요
//         // get().APIBinanceTicker()
//       },
//       APIBinanceTicker: async () => {
//         // API 호출 구현 필요
//         const res: BinanceAPI_SPOT_ticker_24hr_Res[] = await BinanceAPI_SPOT_ticker_24hr()
//         get().setTicker24(res)
//         get().setTicker24Arr(res)
//       },
//       setTicker24: (args: BinanceAPI_SPOT_ticker_24hr_Res[]) => {
//         const data = new Map<string, BinanceAPI_SPOT_ticker_24hr_Res>()
//         args.forEach((el) => {
//           if (IsSpotAssetInfo(el.symbol) && el.symbol.endsWith('USDT')) {
//             data.set(el.symbol, el)
//           }
//         })
//         set({ ticker24Map: data })
//       },
//       setTicker24Arr: (args: BinanceAPI_SPOT_ticker_24hr_Res[]) => {
//         const tmp: spotTickerArray[] = []
//         const tmpheader: spotHeaderInfo = defaultHeader
//         args.forEach((el) => {
//           if (IsSpotAssetInfo(el.symbol) && el.symbol.endsWith('USDT')) {
//             tmp.push({
//               symbol: el.symbol,
//               price: Spot_ConvertPriceTodotSize(el.symbol, Number(el.lastPrice)),
//               rate: ConvetDotSize(el.priceChangePercent, 2),
//               quoteVolume: el.quoteVolume,
//               Favorites: false,
//             })
//           }

//           if (el.symbol === get().spotSymbol) {
//             let pricesize = 2
//             const ticksize = storeSpotExchangeInfo.getState().getTickInfo(el.symbol)
//             if (ticksize !== null) {
//               pricesize = ticksize?.priceTick || 2
//             }

//             tmpheader.symbol = el.symbol
//             tmpheader.price = ConvetDotSize(el.lastPrice, pricesize)
//             tmpheader.persent = el.priceChangePercent
//             tmpheader.diff = ConvetDotSize(el.priceChange, pricesize)
//             tmpheader.high = ConvetDotSize(el.highPrice, pricesize)
//             tmpheader.low = ConvetDotSize(el.lowPrice, pricesize)
//             tmpheader.volumeUSDT = ConvetDotSize(el.volume, 2)
//             tmpheader.markPrice = ConvetDotSize(el.lastPrice, pricesize)
//           }
//         })

//         set({ ticker24Arr: tmp, headerInfo: tmpheader })
//       },

//       setWSTicker24Map: throttle((args: SpotWebSocketTicker[]) => {
//         if (args === null || args === undefined || args.length === 0) {
//           return
//         }

//         // const tikcermap = new Map(get().ticker24Map)
//         // args.map((el) => {
//         //   if (el.e == '24hrTicker') {
//         //     let CHK = ''
//         //     let len = el.s.length
//         //     let pos = len - 4
//         //     CHK = el.s.substring(pos, len)
//         //     if (CHK == 'USDT') {
//         //       tikcermap.set(el.s, mapWebSocketToAPIData(el))
//         //       if (el.s === get().spotSymbol) {
//         //         get().setWSHeader(el)
//         //       }
//         //     }
//         //     const data = mapWebSocketToAPIData(el)
//         //     return data
//         //   }
//         // })

//         // set({ ticker24Map: tikcermap })
//       }, 500),
//       setBiananceSpotSymbol: (symbol: string) => {
//         console.log('symbol :', symbol)
//         const header = get().getHeaderForTicker(symbol)
//         if (header !== null) {
//           set({ spotSymbol: symbol, headerInfo: header })
//           return
//         }
//         set({ spotSymbol: symbol })
//       },
//       getHeaderForTicker: (symbol: string): spotHeaderInfo | null => {
//         const tickerMap = get().ticker24Map

//         const oneTicker = tickerMap.get(symbol)
//         if (oneTicker === undefined) {
//           return null
//         }

//         // 필터로 소수점 처리해야됨 ( 가격만 있으면 될듯 )
//         const filters = storeSpotExchangeInfo.getState().getTickInfo(symbol)
//         if (filters === null || filters === undefined) {
//           return null
//         }

//         const tmpheader: spotHeaderInfo = {
//           symbol: oneTicker.symbol,
//           price: ConvetDotSize(oneTicker.lastPrice, filters.priceTick),
//           persent: oneTicker.priceChangePercent,
//           diff: oneTicker.priceChange,
//           high: ConvetDotSize(oneTicker.highPrice, filters.priceTick),
//           low: ConvetDotSize(oneTicker.lowPrice, filters.priceTick),
//           volumeUSDT: oneTicker.volume,
//           markPrice: oneTicker.lastPrice,
//         }

//         return tmpheader
//       },
//       // 데이터는 주는쪽이 막주고,  받는쪽에서 컨트롤 하는편이 좋음 ..
//       setWSHeader: (args: SpotWebSocketTicker) => {
//         // 헤더 설정 로직 구현 필요
//         const tmpheader = mapWebSocketDataToHeader(args)
//         set({ headerInfo: tmpheader })
//       },
//       wsInit: () => {
//         if (get().wsSocket?.current !== null && get().wsSocket?.current !== undefined) {
//           return
//         }

//         const socket = new WebSocket(process.env.NEXT_PUBLIC_BIANANCE_SPOT_SOCKET_URL)
//         socket.onopen = () => get().wsOpen(socket)
//         socket.onclose = () => get().wsClose()
//         socket.onerror = (event: Event) => get().wsError((event as ErrorEvent).message)
//         socket.onmessage = (event: MessageEvent) => {
//           get().wsMessage(event.data)
//         }

//         set({ wsSocket: { current: socket } })
//       },
//       wsOpen: async (socket: WebSocket) => {
//         subscribeTicker(socket)
//         set({ isConnected: true, retryCount: 0 })
//       },
//       wsClose: () => {
//         set({ isConnected: false })
//       }, // 종료시 리턴
//       wsError: (error: string) => {
//         // console.log("error : ", error);
//         // get().setErrorMessage("wsError", error);
//         set({ isConnected: false, wsSocket: null })
//         const retryLimit = 5
//         const currentRetries = get().retryCount || 0
//         if (currentRetries < retryLimit) {
//           setTimeout(() => {
//             // console.log(`Retrying WebSocket connection... Attempt ${currentRetries + 1}`);
//             set({ retryCount: currentRetries + 1 }) // 재시도 횟수 증가
//             get().wsInit() // WebSocket 재연결
//           }, 1000) // 1초 후 재연결 시도
//         } else {
//           // get().setErrorMessage("wsError", error+"<br>plz reload page");
//         }
//         // 에러시 리턴
//       },
//       wsMessage: (messgae: string) => {
//         const data = JSON.parse(messgae)
//         if (!Array.isArray(data)) {
//           if (data?.result === null || data?.result === undefined) {
//             console.log(data?.result)
//             return
//           }
//           if (data?.e !== null) {
//             get().setWSTicker24Map(data as SpotWebSocketTicker[])
//           }
//         } else {
//           get().setWSTicker24Map(data as SpotWebSocketTicker[])
//         }
//       },
//     }),
//     {
//       name: localhostName,
//       storage: createJSONStorage(() => localStorage),
//       // partialize: (state) => ({
//       //   // spotSymbol: state.spotSymbol,
//       // }),
//     },
//   ),
// )
